import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  UserGroupIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  CogIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { API_URL } from '../constants/urls';
interface SystemStats {
  total_users: number;
  total_organizations: number;
  active_users: number;
  users_by_role: Record<string, number>;
  organizations_by_plan: Record<string, number>;
}

const SuperAdminDashboard: React.FC = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      if (!user || user.role !== 'super_admin') {
        setError('Access denied: Super admin role required');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/admin/stats/system`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch stats: ${response.statusText}`);
        }

        const data = await response.json();
        setStats(data);
      } catch (err) {
        // console.error removed
        setError(err instanceof Error ? err.message : 'Failed to fetch stats');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user, token, API_URL]);

  if (!user || user.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Access Denied
          </h1>
          <p className="text-gray-600">
            This area requires super admin privileges.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.total_users || 0,
      icon: UserGroupIcon,
      color: 'bg-blue-500',
    },
    {
      title: 'Total Organizations',
      value: stats?.total_organizations || 0,
      icon: BuildingOfficeIcon,
      color: 'bg-green-500',
    },
    {
      title: 'Active Users',
      value: stats?.active_users || 0,
      icon: ChartBarIcon,
      color: 'bg-purple-500',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-3xl font-bold text-gray-900">
          Super Admin Dashboard
        </h1>
        <p className="text-gray-600 mt-2">
          System overview and administrative controls
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {statCards.map((card) => (
          <div
            key={card.title}
            className="bg-white rounded-lg shadow p-6 border border-gray-200"
          >
            <div className="flex items-center">
              <div className={`${card.color} rounded-md p-3`}>
                <card.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  {card.title}
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {card.value}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* User Roles Breakdown */}
      {stats?.users_by_role && (
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Users by Role
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(stats.users_by_role).map(([role, count]) => (
                <div key={role} className="text-center">
                  <div className="text-2xl font-bold text-gray-900">
                    {count}
                  </div>
                  <div className="text-sm text-gray-500 capitalize">
                    {role}s
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Organizations by Plan */}
      {stats?.organizations_by_plan && (
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Organizations by Plan
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(stats.organizations_by_plan).map(
                ([plan, count]) => (
                  <div key={plan} className="text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {count}
                    </div>
                    <div className="text-sm text-gray-500 capitalize">
                      {plan} Plan
                    </div>
                  </div>
                ),
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <a
              href="/admin/organizations"
              className="flex items-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <BuildingOfficeIcon className="h-8 w-8 text-blue-500 mr-3" />
              <div>
                <div className="font-medium text-gray-900">
                  Manage Organizations
                </div>
                <div className="text-sm text-gray-500">
                  View and edit all organizations
                </div>
              </div>
            </a>

            <a
              href="/admin/users"
              className="flex items-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <UserGroupIcon className="h-8 w-8 text-green-500 mr-3" />
              <div>
                <div className="font-medium text-gray-900">Manage Users</div>
                <div className="text-sm text-gray-500">
                  View and edit all user accounts
                </div>
              </div>
            </a>

            <a
              href="/admin/system"
              className="flex items-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <CogIcon className="h-8 w-8 text-purple-500 mr-3" />
              <div>
                <div className="font-medium text-gray-900">System Settings</div>
                <div className="text-sm text-gray-500">
                  Configure system-wide settings
                </div>
              </div>
            </a>

            <a
              href="/admin/billing"
              className="flex items-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ChartBarIcon className="h-8 w-8 text-orange-500 mr-3" />
              <div>
                <div className="font-medium text-gray-900">
                  Billing Management
                </div>
                <div className="text-sm text-gray-500">
                  Manage plans and subscriptions
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
